import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

/**
 * Shared member-identity lookup for the PUBLIC registration pages
 * (camps, clinics, events).
 *
 * WHY THIS IS SHARED
 * ------------------
 * These two calls, and the types below, existed as three byte-identical copies
 * in publicCampsAPI / publicClinicsAPI / publicEventsAPI, differing only in the
 * URL segment. That is a security-adjacent flow: `lookupMemberByEmail` used to
 * return member details outright, and moving detail extraction behind
 * `verifyMemberByEmail` + a password was a deliberate SECURITY FIX (see
 * CLAUDE.md, "Detail-extraction has moved behind password auth").
 *
 * Three copies of an auth path means the next hardening — a new status code, a
 * lockout, a rate-limit change, a payload trim — lands on one of three and
 * leaves two public pages on the weaker path. That is the same "fixed on 1 of N"
 * mechanism that produced six enrollment regressions.
 *
 * The per-resource modules now re-export thin wrappers around these, so every
 * existing import keeps working while there is only one implementation.
 */

/** Which public registration surface is asking. Maps to the URL segment. */
export type PublicMemberResource = "camps" | "clinics" | "events";

export interface MemberLookupAthlete {
  id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  gender: string;
  status?: string;
}

export interface MemberLookupResult {
  success: boolean;
  found: boolean;
  // Set when an account exists for this email — frontend must prompt
  // for the password and call verifyMemberByEmail before any data
  // becomes available.
  requires_password?: boolean;
  account_type?: "member" | "customer";
  data?: {
    membership_id?: string;
    // Signed by the backend on a successful verify-member. It is the ONLY thing
    // that lets a public registration name a membership_id: the register and
    // batch-register routes are anonymous, so without it any caller could put
    // registrations (and invoices) on any member. Short-lived; send it back
    // as `member_token` alongside membership_id.
    member_token?: string;
    contact_name: string;
    contact_phone: string | null;
    athletes: MemberLookupAthlete[];
  };
}

export interface MemberVerifyResult extends MemberLookupResult {}

/**
 * Existence probe — returns { found, requires_password, account_type }.
 * NO PII is returned by design. The caller must call verifyMemberByEmail with a
 * password before any name/phone/athlete data becomes available. Do not add
 * member details to this response.
 */
export const lookupPublicMemberByEmail = async (
  resource: PublicMemberResource,
  tenantId: string,
  email: string
): Promise<MemberLookupResult> => {
  const response = await axios.post(
    `${BASE_URL}/public/${resource}/lookup-member`,
    { email },
    {
      headers: {
        "x-tenant-id": tenantId,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

/**
 * Validate the password and return full member/customer data.
 * Throws on failure — 401 bad credentials, 429 rate-limited. Callers surface
 * those distinctly; do not swallow them.
 */
export const verifyPublicMemberByEmail = async (
  resource: PublicMemberResource,
  tenantId: string,
  email: string,
  password: string
): Promise<MemberVerifyResult> => {
  const response = await axios.post(
    `${BASE_URL}/public/${resource}/verify-member`,
    { email, password },
    {
      headers: {
        "x-tenant-id": tenantId,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};
